/**
===================================================
Module: frontend.src.contexts.VisualizerContext.tsx
Description: Visualizer context provider for 3D scene state management
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { apiService } from "../services/api";
import {
  VisualizerContextType,
  VisualizerState,
  LoadingPhase,
  SessionData,
  VisualizationSettings,
  VISUALIZER_CONFIG,
} from "../types/visualizer";

// Add notification interface
interface NotificationContextType {
  addError: (message: string) => void;
  addSuccess: (message: string) => void;
  addInfo: (message: string) => void;
}

// Initial state
const initialVisualizationSettings: VisualizationSettings = {
  showLandmarks: true,
  showFrustums: true,
  showTrajectory: true,
  showDensePoints: true,
  sparsePointSizeUI: VISUALIZER_CONFIG.DEFAULT_SPARSE_POINT_SIZE,
  densePointSizeUI: VISUALIZER_CONFIG.DEFAULT_DENSE_POINT_SIZE,
  frustumScaleUI: VISUALIZER_CONFIG.DEFAULT_FRUSTUM_SCALE,
};

const initialState: VisualizerState = {
  sessionData: null,
  currentKeyframeIndex: 0,
  loadingPhase: LoadingPhase.INITIAL,
  error: null,
  visualizationSettings: initialVisualizationSettings,
  sceneReady: false,
  visualizationReady: false,
  loadedKeyframes: new Set(),
  downloadingKeyframes: new Set(),
};

// Action types
type VisualizerAction =
  | { type: "SET_LOADING_PHASE"; payload: LoadingPhase }
  | { type: "SET_SESSION_DATA"; payload: SessionData }
  | { type: "SET_CURRENT_KEYFRAME"; payload: number }
  | { type: "SET_ERROR"; payload: string | null }
  | {
      type: "UPDATE_VISUALIZATION_SETTINGS";
      payload: Partial<VisualizationSettings>;
    }
  | { type: "SET_SCENE_READY"; payload: boolean }
  | { type: "SET_VISUALIZATION_READY"; payload: boolean }
  | { type: "ADD_LOADED_KEYFRAME"; payload: number }
  | { type: "REMOVE_LOADED_KEYFRAME"; payload: number }
  | { type: "ADD_DOWNLOADING_KEYFRAME"; payload: number }
  | { type: "REMOVE_DOWNLOADING_KEYFRAME"; payload: number }
  | { type: "RESET_STATE" };

// Reducer
function visualizerReducer(
  state: VisualizerState,
  action: VisualizerAction,
): VisualizerState {
  switch (action.type) {
    case "SET_LOADING_PHASE":
      return { ...state, loadingPhase: action.payload };

    case "SET_SESSION_DATA":
      return { ...state, sessionData: action.payload };

    case "SET_CURRENT_KEYFRAME":
      return { ...state, currentKeyframeIndex: action.payload };

    case "SET_ERROR":
      return { ...state, error: action.payload };

    case "UPDATE_VISUALIZATION_SETTINGS":
      return {
        ...state,
        visualizationSettings: {
          ...state.visualizationSettings,
          ...action.payload,
        },
      };

    case "SET_SCENE_READY":
      return { ...state, sceneReady: action.payload };

    case "SET_VISUALIZATION_READY":
      return { ...state, visualizationReady: action.payload };

    case "ADD_LOADED_KEYFRAME":
      const newLoadedSet = new Set(state.loadedKeyframes);
      newLoadedSet.add(action.payload);
      return {
        ...state,
        loadedKeyframes: newLoadedSet,
      };

    case "REMOVE_LOADED_KEYFRAME":
      const newLoaded = new Set(state.loadedKeyframes);
      newLoaded.delete(action.payload);
      return { ...state, loadedKeyframes: newLoaded };

    case "ADD_DOWNLOADING_KEYFRAME":
      const newDownloadingSet = new Set(state.downloadingKeyframes);
      newDownloadingSet.add(action.payload);
      return {
        ...state,
        downloadingKeyframes: newDownloadingSet,
      };

    case "REMOVE_DOWNLOADING_KEYFRAME":
      const newDownloading = new Set(state.downloadingKeyframes);
      newDownloading.delete(action.payload);
      return { ...state, downloadingKeyframes: newDownloading };

    case "RESET_STATE":
      return { ...initialState };

    default:
      return state;
  }
}

// Context
const VisualizerContext = createContext<VisualizerContextType | undefined>(
  undefined,
);

// Hook
export const useVisualizer = () => {
  const context = useContext(VisualizerContext);
  if (context === undefined) {
    throw new Error("useVisualizer must be used within a VisualizerProvider");
  }
  return context;
};

// Provider component
interface VisualizerProviderProps {
  children: React.ReactNode;
  sessionId: string;
}

export const VisualizerProvider: React.FC<VisualizerProviderProps> = ({
  children,
  sessionId,
}) => {
  const [state, dispatch] = useReducer(visualizerReducer, initialState);

  // Refs for cancellation and memory management
  const abortControllerRef = useRef<AbortController | null>(null);
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resetCameraRef = useRef<(() => void) | null>(null);
  const isRefreshingUrlsRef = useRef<boolean>(false);

  const notificationContextRef = useRef<NotificationContextType | null>(null);

  const registerNotifications = useCallback(
    (notifications: NotificationContextType) => {
      notificationContextRef.current = notifications;
    },
    [],
  );

  const parseMapFile = useCallback(async (data: Uint8Array) => {
    try {
      let parsed: any;
      try {
        const msgpack = await import("msgpack-lite");
        parsed = msgpack.decode(data);
      } catch (importError) {
        throw new Error("Cannot parse map file: msgpack-lite not available");
      }

      if (parsed.keyframes && parsed.landmark_points) {
        return {
          keyframes: parsed.keyframes,
          landmark_points: parsed.landmark_points,
          dense_points: parsed.dense_points || [],
        };
      } else {
        return {
          keyframes: [],
          landmark_points: [],
          dense_points: [],
        };
      }
    } catch (error) {
      return {};
    }
  }, []);

  // Core actions
  const initializeSession = useCallback(
    async (sessionId: string) => {
      try {
        dispatch({
          type: "SET_LOADING_PHASE",
          payload: LoadingPhase.FETCHING_SESSION_DATA,
        });
        dispatch({ type: "SET_ERROR", payload: null });

        const response = await apiService.getSessionResults(sessionId, 0, -1);

        if (response.keyframes_array && response.keyframes_array.length > 0) {
          const sortedKeyframes = response.keyframes_array.sort(
            (a, b) => a.frame_index - b.frame_index,
          );

          const sessionData: SessionData = {
            keyframes: sortedKeyframes,
            mapUrl: response.map_url || "",
          };

          dispatch({ type: "SET_SESSION_DATA", payload: sessionData });

          if (response.map_url) {
            dispatch({
              type: "SET_LOADING_PHASE",
              payload: LoadingPhase.DOWNLOADING_MAP,
            });

            try {
              const mapResponse = await fetch(response.map_url);
              const mapData = await mapResponse.arrayBuffer();
              const uint8Array = new Uint8Array(mapData);

              const parsedData = await parseMapFile(uint8Array);

              const updatedSessionData: SessionData = {
                ...sessionData,
                parsedMapData: {
                  mapData: uint8Array,
                  ...parsedData,
                },
              };

              dispatch({
                type: "SET_SESSION_DATA",
                payload: updatedSessionData,
              });
            } catch (mapError) {
              if (notificationContextRef.current) {
                notificationContextRef.current.addError(
                  `3Dマップデータのダウンロードに失敗しました`,
                );
              }
            }
          } else {
            if (notificationContextRef.current) {
              notificationContextRef.current.addError(
                "3Dマップデータが存在しません",
              );
            }
          }

          dispatch({
            type: "SET_LOADING_PHASE",
            payload: LoadingPhase.DOWNLOADING_KEYFRAMES,
          });
          try {
            await downloadVisibleKeyframes(0, sortedKeyframes);
          } catch (keyframeError) {
            if (notificationContextRef.current) {
              notificationContextRef.current.addError(
                `データのロードに失敗しました}`,
              );
            }
          }

          dispatch({
            type: "SET_LOADING_PHASE",
            payload: LoadingPhase.RENDERING_COMPONENTS,
          });
        } else {
          if (notificationContextRef.current) {
            notificationContextRef.current.addError(
              "データが存在しません",
            );
          }

          const emptySessionData: SessionData = {
            keyframes: [],
            mapUrl: response.map_url || "",
          };

          dispatch({ type: "SET_SESSION_DATA", payload: emptySessionData });

          if (response.map_url) {
            try {
              const mapResponse = await fetch(response.map_url);
              const mapData = await mapResponse.arrayBuffer();
              const uint8Array = new Uint8Array(mapData);

              const parsedData = await parseMapFile(uint8Array);

              const updatedSessionData: SessionData = {
                ...emptySessionData,
                parsedMapData: {
                  mapData: uint8Array,
                  ...parsedData,
                },
              };

              dispatch({
                type: "SET_SESSION_DATA",
                payload: updatedSessionData,
              });
            } catch (mapError) {
              if (notificationContextRef.current) {
                notificationContextRef.current.addError(
                  `Failed to load 3D map data: ${mapError instanceof Error ? mapError.message : "Unknown error"}`,
                );
              }
            }
          }

          dispatch({
            type: "SET_LOADING_PHASE",
            payload: LoadingPhase.RENDERING_COMPONENTS,
          });
        }
      } catch (error) {
        let errorMessage = "Failed to load session";
        if (error instanceof Error) {
          if (
            error.message.includes("404") ||
            error.message.includes("not found")
          ) {
            errorMessage = "Session not found or has no processed results";
          } else if (
            error.message.includes("403") ||
            error.message.includes("Forbidden")
          ) {
            errorMessage = "Access denied to this session";
          } else {
            errorMessage = `Failed to load session: ${error.message}`;
          }
        }

        if (notificationContextRef.current) {
          notificationContextRef.current.addError(errorMessage);
        }

        const emptySessionData: SessionData = {
          keyframes: [],
          mapUrl: "",
        };
        dispatch({ type: "SET_SESSION_DATA", payload: emptySessionData });
        dispatch({ type: "SET_LOADING_PHASE", payload: LoadingPhase.READY });
      }
    },
    [parseMapFile],
  );

  const downloadVisibleKeyframes = useCallback(
    async (centerIndex: number, keyframes: any[]) => {
      const visibleRange = VISUALIZER_CONFIG.KEYFRAME_CAROUSEL_VISIBLE_RANGE;
      const startIndex = Math.max(0, centerIndex - visibleRange);
      const endIndex = Math.min(
        keyframes.length - 1,
        centerIndex + visibleRange,
      );

      const downloadPromises = [];

      for (let i = startIndex; i <= endIndex; i++) {
        if (
          !state.loadedKeyframes.has(i) &&
          !state.downloadingKeyframes.has(i)
        ) {
          dispatch({ type: "ADD_DOWNLOADING_KEYFRAME", payload: i });

          const keyframe = keyframes[i];
          const downloadPromise = downloadKeyframeImage(keyframe, i);
          downloadPromises.push(downloadPromise);
        }
      }

      await Promise.allSettled(downloadPromises);
    },
    [state.loadedKeyframes, state.downloadingKeyframes],
  );

  const downloadKeyframeImage = useCallback(
    async (keyframe: any, index: number): Promise<void> => {
      let retries = 0;

      while (retries < VISUALIZER_CONFIG.MAX_RETRIES) {
        try {
          // Create or update img element in DOM
          let imgElement = document.getElementById(
            `thumb-${keyframe.keyframe_id}`,
          ) as HTMLImageElement;

          if (!imgElement) {
            imgElement = document.createElement("img");
            imgElement.id = `thumb-${keyframe.keyframe_id}`;
            imgElement.className = "keyframe-thumb";
            imgElement.crossOrigin = "anonymous";
          }

          // Promise-based image loading
          await new Promise<void>((resolve, reject) => {
            imgElement.onload = () => {
              dispatch({ type: "ADD_LOADED_KEYFRAME", payload: index });
              dispatch({ type: "REMOVE_DOWNLOADING_KEYFRAME", payload: index });
              resolve();
            };

            imgElement.onerror = async () => {
              // Check for 403 error (expired URL)
              try {
                const response = await fetch(keyframe.url, { method: "HEAD" });
                if (response.status === 403) {
                  if (!isRefreshingUrlsRef.current) {
                    isRefreshingUrlsRef.current = true;
                    state.downloadingKeyframes.forEach((downloadingIndex) => {
                      dispatch({
                        type: "REMOVE_DOWNLOADING_KEYFRAME",
                        payload: downloadingIndex,
                      });
                    });

                    dispatch({
                      type: "SET_LOADING_PHASE",
                      payload: LoadingPhase.DOWNLOADING_KEYFRAMES,
                    });

                    try {
                      await refreshUrls();

                      isRefreshingUrlsRef.current = false;

                      dispatch({
                        type: "SET_LOADING_PHASE",
                        payload: LoadingPhase.READY,
                      });

                      return;
                    } catch (refreshError) {
                      isRefreshingUrlsRef.current = false;
                      dispatch({
                        type: "SET_LOADING_PHASE",
                        payload: LoadingPhase.READY,
                      });
                      reject(new Error(`URL refresh failed: ${refreshError}`));
                      return;
                    }
                  } else {
                    reject(new Error("URL refresh in progress"));
                    return;
                  }
                }
              } catch (fetchError) {
                // Ignore fetch errors when checking URL status
              }

              reject(
                new Error(`Failed to load keyframe ${keyframe.keyframe_id}`),
              );
            };

            const currentKeyframe = state.sessionData?.keyframes[index];
            const urlToUse = currentKeyframe
              ? currentKeyframe.url
              : keyframe.url;
            imgElement.src = urlToUse;
          });

          break;
        } catch (error) {
          retries++;

          if (retries >= VISUALIZER_CONFIG.MAX_RETRIES) {
            dispatch({ type: "REMOVE_DOWNLOADING_KEYFRAME", payload: index });
            throw error;
          }

          await new Promise((resolve) => setTimeout(resolve, 1000 * retries));
        }
      }
    },
    [state.sessionData, state.downloadingKeyframes],
  );

  const navigateToKeyframe = useCallback(
    async (index: number) => {
      if (
        !state.sessionData ||
        index < 0 ||
        index >= state.sessionData.keyframes.length
      ) {
        return;
      }

      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }

      dispatch({ type: "SET_LOADING_PHASE", payload: LoadingPhase.NAVIGATING });
      dispatch({ type: "SET_CURRENT_KEYFRAME", payload: index });

      try {
        // Download visible range around new keyframe
        await downloadVisibleKeyframes(index, state.sessionData.keyframes);

        // Memory management - unload distant keyframes
        manageKeyframeMemory(index);

        // Set back to ready after a brief delay to ensure rendering completes
        navigationTimeoutRef.current = setTimeout(() => {
          dispatch({ type: "SET_LOADING_PHASE", payload: LoadingPhase.READY });
        }, 100);
      } catch (error) {
        if (notificationContextRef.current) {
          notificationContextRef.current.addError(
            `読み込みに失敗しました`,
          );
        }
        dispatch({ type: "SET_LOADING_PHASE", payload: LoadingPhase.READY });
      }
    },
    [state.sessionData, downloadVisibleKeyframes],
  );

  // Memory management
  const manageKeyframeMemory = useCallback(
    (currentIndex: number) => {
      if (
        state.loadedKeyframes.size <= VISUALIZER_CONFIG.MAX_KEYFRAMES_IN_MEMORY
      ) {
        return;
      }

      const visibleRange =
        VISUALIZER_CONFIG.KEYFRAME_CAROUSEL_VISIBLE_RANGE * 3; // Keep wider range
      const keepStart = Math.max(0, currentIndex - visibleRange);
      const keepEnd = Math.min(
        state.sessionData?.keyframes.length || 0,
        currentIndex + visibleRange,
      );

      // Unload keyframes outside the keep range
      state.loadedKeyframes.forEach((loadedIndex) => {
        if (loadedIndex < keepStart || loadedIndex > keepEnd) {
          const imgElement = document.getElementById(
            `thumb-${state.sessionData?.keyframes[loadedIndex]?.keyframe_id}`,
          );
          if (imgElement) {
            imgElement.remove();
          }
          dispatch({ type: "REMOVE_LOADED_KEYFRAME", payload: loadedIndex });
        }
      });
    },
    [state.loadedKeyframes, state.sessionData],
  );

  // URL refresh function
  const refreshUrls = useCallback(async () => {
    if (!sessionId || !state.sessionData) return;

    try {
      const response = await apiService.getSessionResults(sessionId, 0, -1);

      if (response.keyframes_array && response.keyframes_array.length > 0) {
        const updatedKeyframes = response.keyframes_array.sort(
          (a, b) => a.frame_index - b.frame_index,
        );

        const updatedSessionData: SessionData = {
          ...state.sessionData,
          keyframes: updatedKeyframes,
          mapUrl: response.map_url || state.sessionData.mapUrl,
        };

        dispatch({ type: "SET_SESSION_DATA", payload: updatedSessionData });
      }
    } catch (error) {
      if (notificationContextRef.current) {
        notificationContextRef.current.addError(
          `読み込みに失敗しました`,
        );
      }
    }
  }, [sessionId, state.sessionData]);

  // Other action functions
  const updateVisualizationSettings = useCallback(
    (settings: Partial<VisualizationSettings>) => {
      dispatch({ type: "UPDATE_VISUALIZATION_SETTINGS", payload: settings });
    },
    [],
  );

  const resetCamera = useCallback(() => {
    if (resetCameraRef.current) {
      resetCameraRef.current();
    }
  }, []);

  const registerResetCamera = useCallback((resetFn: () => void) => {
    resetCameraRef.current = resetFn;
  }, []);

  const setSceneReady = useCallback((ready: boolean) => {
    dispatch({ type: "SET_SCENE_READY", payload: ready });
  }, []);

  const setVisualizationReady = useCallback((ready: boolean) => {
    dispatch({ type: "SET_VISUALIZATION_READY", payload: ready });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: "SET_ERROR", payload: error });
  }, []);

  const addError = useCallback((message: string) => {
    if (notificationContextRef.current) {
      notificationContextRef.current.addError(message);
    }
  }, []);

  const addSuccess = useCallback((message: string) => {
    if (notificationContextRef.current) {
      notificationContextRef.current.addSuccess(message);
    }
  }, []);

  const addInfo = useCallback((message: string) => {
    if (notificationContextRef.current) {
      notificationContextRef.current.addInfo(message);
    }
  }, []);

  React.useEffect(() => {
    if (sessionId && state.loadingPhase === LoadingPhase.INITIAL) {
      initializeSession(sessionId);
    }
  }, [sessionId]);

  React.useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
      // Reset URL refresh flag
      isRefreshingUrlsRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      state.sceneReady &&
      state.visualizationReady &&
      state.loadingPhase === LoadingPhase.RENDERING_COMPONENTS
    ) {
      dispatch({ type: "SET_LOADING_PHASE", payload: LoadingPhase.READY });
    }
  }, [state.sceneReady, state.visualizationReady, state.loadingPhase]);

  const value: VisualizerContextType = {
    state,
    initializeSession,
    navigateToKeyframe,
    refreshUrls,
    updateVisualizationSettings,
    setSceneReady,
    setVisualizationReady,
    resetCamera,
    registerResetCamera,
    registerNotifications,
    setError,
    addError,
    addSuccess,
    addInfo,
  };

  return (
    <VisualizerContext.Provider value={value}>
      {children}
    </VisualizerContext.Provider>
  );
};
